import { Injectable } from '@angular/core';
import { CanvasControllerService } from '../../../../../services/canvas-controller/canvas-controller.service';
import { EmbedData } from '../../../edit-layout/common/data/embed';
import {
  BaseModel,
  PartModel,
  ThreedProject,
} from '../../../workspace-layout/commons/services/project-controller/project-controller';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';
import { BehaviorSubject, take } from 'rxjs';
import { PartListControllerService } from '../../../workspace-layout/commons/services/part-list-controller/part-list-controller.service';

@Injectable({
  providedIn: 'root',
})
export class EmbedService {
  public _isLoading: boolean = true;

  public _selectedProejctIndex: number = 0;

  public _embedData: EmbedData | undefined;

  public projectDataMap: Map<string, ThreedProject> = new Map();

  public selectedProjectIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public embedData$: BehaviorSubject<EmbedData | undefined> = new BehaviorSubject<
    EmbedData | undefined
  >(undefined);
  public project$: BehaviorSubject<ThreedProject | undefined> = new BehaviorSubject<
    ThreedProject | undefined
  >(undefined);

  constructor(
    private partListController: PartListControllerService,
    private canvasController: CanvasControllerService,
    private threeGraphql: ThreeGraphqlService,
  ) {}

  get isLoading(): boolean {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    this._isLoading = value;
    this.isLoading$.next(this._isLoading);
  }

  get embedData(): EmbedData | undefined {
    return this._embedData;
  }

  set embedData(value: EmbedData | undefined) {
    this._embedData = value;
    this.embedData$.next(this._embedData);
  }

  get selectedProejctIndex(): number {
    return this._selectedProejctIndex;
  }

  set selectedProejctIndex(value: number) {
    this._selectedProejctIndex = value;
    this.selectedProjectIndex$.next(this._selectedProejctIndex);
  }

  //--------------------------------------------------------------------------------------------------------
  // @ Public Method
  //--------------------------------------------------------------------------------------------------------

  public initEmbedData(embedData: EmbedData): void {
    this.embedData = embedData;
    this.embedData$.next(this.embedData);
  }

  public selectedProejct(uuid: string): void {
    this.threeGraphql
      .getEmbedProjectByUuid(uuid)
      .pipe(take(1))
      .subscribe((project: any) => {
        if (!project) return;
        this.project$.next(project);
        if (!project.baseModels) return;
        if (!project.baseModels[0].meshes) {
          this.threeGraphql
            .getBaseModel(project.baseModels[0].uuid)
            .pipe(take(1))
            .subscribe((baseModel: BaseModel | undefined) => {
              if (!project.baseModels || !baseModel) return;
              if (!this.embedData) return;
              project.baseModels[0] = baseModel;
              this.embedData.projects[this.selectedProejctIndex] = project;
              this.embedData = this.embedData;
              this.canvasController.addModel(baseModel, true).then((object) => {
                if (!object) return;
                this.canvasController.getObjectRatio(object, baseModel?.width || 0);

                if (!this.embedData) return;
                if (!this.embedData.projects) return;
                if (this.embedData.projects.length === 0) return;
                if (!this.embedData.projects[this.selectedProejctIndex]) return;
                if (this.embedData.projects[this.selectedProejctIndex].mainPreset) {
                  this.threeGraphql
                    .getPreset(this.embedData.projects[this.selectedProejctIndex].mainPreset.uuid)
                    .pipe(take(1))
                    .subscribe((preset: any) => {
                      const settings = JSON.parse(preset.setting);
                      const stickers = preset.stickers.map((sticker: any) => JSON.parse(sticker));
                      settings.forEach((setting: any) => {
                        if (setting.style.type == 'color')
                          this.canvasController.materialsColorChange(
                            [setting.material],
                            setting.style.value,
                          );
                      });

                      stickers.forEach((stickerData: any) => {
                        if (stickerData.isDualSided)
                          this.canvasController.canvas?.attachOneSideDecal(
                            stickerData.position,
                            stickerData.rotation,
                            stickerData.scale,
                            stickerData.scaleRatio,
                            stickerData.attachMeshNames,
                            stickerData.stickerUrl,
                            stickerData.renderOrder,
                          );
                        else
                          this.canvasController.canvas?.attachOneSideDecal(
                            stickerData.position,
                            stickerData.rotation,
                            stickerData.scale,
                            stickerData.scaleRatio,
                            stickerData.attachMeshNames,
                            stickerData.stickerUrl,
                            stickerData.renderOrder,
                          );
                      });

                      preset.partModelUuids.forEach((uuid: string) => {
                        if (!this.embedData) return;
                        if (!this.embedData.projects) return;
                        if (this.embedData.projects.length === 0) return;
                        if (!this.embedData.projects[this.selectedProejctIndex]) return;
                        const partModel = this.embedData.projects[
                          this.selectedProejctIndex
                        ].partModels.find((model: PartModel) => model.uuid === uuid);
                        if (!partModel)
                          this.threeGraphql
                            .getPartModel(uuid)
                            .pipe(take(1))
                            .subscribe((model: PartModel | undefined) => {
                              if (!model) return;
                              console.log(model);
                              this.canvasController.addModel(model).then(() => {
                                model.isSelected = true;
                                this.canvasController.stateManager.addModel(model);
                                this.partListController.addPart(model);
                              });
                            });
                      });
                    });
                }
              });
            });
        } else {
          this.canvasController.addModel(project.baseModels[0], true).then((object) => {
            if (!object) return;
            this.canvasController.getObjectRatio(object, project.baseModels![0].width || 0);

            if (!this.embedData) return;
            if (!this.embedData.projects) return;
            if (this.embedData.projects.length === 0) return;
            if (!this.embedData.projects[this.selectedProejctIndex]) return;
            if (this.embedData.projects[this.selectedProejctIndex].mainPreset) {
              this.threeGraphql
                .getPreset(this.embedData.projects[this.selectedProejctIndex].mainPreset.uuid)
                .pipe(take(1))
                .subscribe((preset: any) => {
                  console.log(preset);
                  const settings = JSON.parse(preset.setting);
                  const stickers = preset.stickers.map((sticker: any) => JSON.parse(sticker));
                  settings.forEach((setting: any) => {
                    if (setting.style.type == 'color')
                      this.canvasController.materialsColorChange(
                        [setting.material],
                        setting.style.value,
                      );
                  });

                  stickers.forEach((stickerData: any) => {
                    if (stickerData.isDualSided)
                      this.canvasController.canvas?.attachOneSideDecal(
                        stickerData.position,
                        stickerData.rotation,
                        stickerData.scale,
                        stickerData.scaleRatio,
                        stickerData.attachMeshNames,
                        stickerData.stickerUrl,
                        stickerData.renderOrder,
                      );
                    else
                      this.canvasController.canvas?.attachOneSideDecal(
                        stickerData.position,
                        stickerData.rotation,
                        stickerData.scale,
                        stickerData.scaleRatio,
                        stickerData.attachMeshNames,
                        stickerData.stickerUrl,
                        stickerData.renderOrder,
                      );
                  });

                  preset.partModelUuids.forEach((uuid: string) => {
                    if (!this.embedData) return;
                    if (!this.embedData.projects) return;
                    if (this.embedData.projects.length === 0) return;
                    if (!this.embedData.projects[this.selectedProejctIndex]) return;
                    console.log(this.embedData.projects[this.selectedProejctIndex]);
                    const partModel = this.embedData.projects[
                      this.selectedProejctIndex
                    ].partModels.find((model: PartModel) => model.uuid === uuid);
                    if (!partModel)
                      this.threeGraphql
                        .getPartModel(uuid)
                        .pipe(take(1))
                        .subscribe((model: PartModel | undefined) => {
                          if (!model) return;
                          this.canvasController.addModel(model).then(() => {
                            model.isSelected = true;
                            this.canvasController.stateManager.addModel(model);
                            this.partListController.addPart(model);
                          });
                        });
                  });
                });
            }
          });
        }
      });
  }

  public updateEmbedData(): void {
    if (!this.embedData) return;
    const input = {
      title: this.embedData.title,
      description: this.embedData.description,
      isAutoRotation: this.embedData.isAutoRotation,
      isCapture: this.embedData.isCapture,
      isFullScreen: this.embedData.isFullScreen,
    };
    this.threeGraphql
      .updateCompanyProjects(this.embedData.uuid, input)
      .pipe(take(1))
      .subscribe((embedData: EmbedData) => {
        if (!embedData) return;
        this.embedData = embedData;
      });
  }

  public selectedPart(part: any, selectedPart: boolean): void {
    if (!this.embedData) return;
    if (!this.embedData.projects) return;
    if (this.embedData.projects.length === 0) return;
    if (!this.embedData.projects[this.selectedProejctIndex]) return;
    const beforePart = this.embedData.projects[this.selectedProejctIndex].partModels.find(
      (model: PartModel) => model.uuid === part.uuid,
    );
    if (!beforePart) return;
    beforePart.isSelected = selectedPart;

    this.embedData = this.embedData;
  }
}
